import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Badge, Descriptions, Divider, Flex } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import {
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { useHasPermission, useHasPermissionForVersion } from '../auth';
import { PermissionCode } from '../user';
import { ApiError } from '../common/ApiClient';
import {
  ActionButton,
  Bleed,
  ContentSegments,
  DetailLayout,
  DropdownItem,
  ErrorAlertWithDrawer,
  InfoIconUrl,
  ObjectHeader,
  Padding,
  PopconfirmDelete,
} from '../common/components';
import { ContentSegmentOption } from '../common/components/segments/ContentSegmentsContext';
import { ContentSegmentTotalEntries } from '../common/components/segments/ContentSegmentTotalEntries';
import { useMutationErrors } from '../common/queryClient';
import { getQueryParam } from '../common/queryParams';
import { formatCodeName, getValidPeriodString } from '../common/utils';
import { GetApiErrorMessage } from '../common/utils/ValidationMessagesMap';
import {
  useSystemClassifierItem,
  useSystemClassifiers,
} from '../configuration';
import { isLocked, LockedBy, ObjectType, useShowLockData } from '../lock';
import { AlgorithmsTable } from './algorithms/table/AlgorithmsTable';
import { AlternativeAlgorithmsViewTable } from './alternatives/AlternativeAlgorithmsViewTable';
import { CalendarView } from './calendar/CalendarView';
import { CloneAlgorithmsModal } from './cloneAlgorithms/CloneAlgorithmsModal';
import {
  DataObjectContextProvider,
  useDataObjectContext,
} from './DataObjectContext';
import { DataObjectVersionSelect } from './DataObjectVersionSelect';
import { DataObjectDescriptionVersionStoreProvider } from './DataObjectDescriptionVersionStore';
import { DeadlinesViewTable } from './deadlines/DeadlinesViewTable';
import { DimensionViewTable } from './dimensions/DimensionViewTable';
import { DataObjectExcelActions } from './excel/DataObjectExcelActions';
import { DataObjectIndicatorDetailsTable } from './indicators/DataObjectIndicatorsDetailsTable';
import { KeysViewTable } from './keys/KeysViewTable';
import { useMutateDataObjectQuery } from './query/useMutateDataObjectQuery';
import { DataSourcesViewTable } from './sources/DataSourcesViewTable';
import translations from './translations/DataObjectDetails.translations.json';
import validationTranslations from './translations/validation.translations.json';
import {
  DataObjectCloneEntityType,
  DataObjectContentSegmentKey,
  DataObjectFormDataType,
  DataObjectOutputType,
  DataObjectType,
  DataObjectView,
} from './types';
import { useDeadlineValidationErrors } from './useDeadlineValidationErrors';
import { getActiveVersion } from './utils';
import { ValidationRulesViewTable } from './validation/ValidationRulesViewTable';
import { useAlertContext } from '../common/form';
import { AlgorithmTestCalculationType } from './algorithms/testRun/schema/types';
import { ValidateRequiredKeys } from './keys/ValidateRequiredKeys';
import { ValidationRuleTestValidationType } from './schema';
import { DataObjectFormTypeBadge } from './DataObjectFormTypeBadge';
import { AlgorithmBasicType } from './algorithms/schema/types';
import {
  type DataObjectPanelNavigationParams,
  getDataObjectPanelNavigationParams,
} from './usePanelParams';

const { addTranslations, t } = i18nNsFactory('data-object-details-layout');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t(`alert.error.code.${code}`, meta?.args && { ...meta.args });

export interface DataObjectDetailLayoutProps {
  dataObjectView: DataObjectView;
  dataObjectVersion: DataObjectFormDataType;
  breadCrumb: ReactElement;
  navigateToDirectory: () => void;
  navigateToDetails: (params: { id?: number; segment?: string }) => void;
  navigateToEdit: (
    params: { id?: number } & DataObjectPanelNavigationParams,
  ) => void;
  navigateToRelationsGraph: () => void;
  onClickClone: (params: {
    entityType: DataObjectCloneEntityType;
    segment?: string;
  }) => void;
  preSubmit: () => void;
  onSuccess: () => void;
  openTestCalculate: (params: AlgorithmTestCalculationType) => void;
  openTestValidate: (params: ValidationRuleTestValidationType) => void;
  actions?: ReactNode;
}

export const DataObjectDetailsLayout: FC<DataObjectDetailLayoutProps> = (
  props,
) => (
  <DataObjectContextProvider>
    <Layout {...props} />
  </DataObjectContextProvider>
);

export const Layout: FC<DataObjectDetailLayoutProps> = (props) => {
  const {
    dataObjectView,
    breadCrumb,
    dataObjectVersion,
    navigateToDirectory,
    navigateToDetails,
    navigateToEdit,
    navigateToRelationsGraph,
    onClickClone,
    preSubmit,
    onSuccess,
    openTestCalculate,
    openTestValidate,
    actions,
  } = props;

  const { lockData } = useShowLockData(
    ObjectType.DataObjectDescriptionVersion,
    dataObjectVersion.versionId,
  );
  const { mutate } = useMutateDataObjectQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const {
    deadlineValidationErrors,
    setDeadlineValidationErrors,
    displayDeadlineValidationType,
  } = useDeadlineValidationErrors();

  const { validateKeysModalOpen, setValidateKeysModalOpen } =
    useDataObjectContext();

  const statusName = useSystemClassifierItem(
    'DATA_OBJ_GRP_VER_STATUS',
    dataObjectVersion.statusCode,
  )?.name;

  const handleVersionChange = (id?: any) => {
    navigateToDetails({ id, segment: getQueryParam('segment') });
  };

  const systemClassifiers = useSystemClassifiers(
    'REPORT_RECIPIENT',
    'CONFIDENTIALITY_STATUS',
    'DOMAIN',
    'FREQUENCY',
    'DATA_ORIGIN',
    'DATA_PROVIDER_GROUP',
    'DATA_OBJ_GRP_VER_STATUS',
  );

  const handleDelete = useCallback(() => {
    resetAlert();
    clearMutationErrors();
    const otherVersions = [...dataObjectVersion.allVersions].filter(
      (item) => item.id !== dataObjectVersion.versionId,
    );
    const nextVersion = getActiveVersion(otherVersions);
    mutate(
      { id: dataObjectVersion.versionId },
      {
        onSuccess: () => {
          if (nextVersion) {
            navigateToDetails({
              id: nextVersion.id,
              segment: getQueryParam('segment'),
            });
          } else {
            navigateToDirectory();
          }
          setSuccess(t('alert.success.deleted'));
        },
        onError: ({ response }) => {
          setMutationErrors(response?.data.errors ?? []);
        },
      },
    );
  }, [
    resetAlert,
    clearMutationErrors,
    dataObjectVersion.allVersions,
    dataObjectVersion.versionId,
    mutate,
    setSuccess,
    navigateToDetails,
    navigateToDirectory,
    setMutationErrors,
  ]);

  const versionCount = dataObjectVersion.allVersions.length;
  const key = dataObjectView.key;
  const deleteGenitiveModelName = useMemo(
    () =>
      versionCount > 1
        ? t('button.deleteVersion.genitiveModelName.version')
        : t('button.deleteVersion.genitiveModelName.' + key),
    [key, versionCount],
  );

  const [isCloneAlgorithmOpen, setIsCloneAlgorithmOpen] = useState(false);
  const [algorithmsCount, setAlgorithmsCount] = useState<number>(
    dataObjectVersion.algorithmsCount,
  );

  const onClickCloneAlgorithm = useCallback(() => {
    preSubmit?.();
    setIsCloneAlgorithmOpen(true);
  }, [setIsCloneAlgorithmOpen, preSubmit]);

  const handleSuccess = useCallback(
    (data: AlgorithmBasicType[]) => {
      setIsCloneAlgorithmOpen(false);
      setAlgorithmsCount(data.length);
      onSuccess();
    },
    [setIsCloneAlgorithmOpen, onSuccess],
  );

  const permissionCode =
    dataObjectVersion.dataObjectType === DataObjectType.IN
      ? PermissionCode.INPUT_DESC_MANAGE
      : PermissionCode.OUTPUT_DESC_MANAGE;
  const hasPermissionToManageVersion = useHasPermissionForVersion(
    permissionCode,
    dataObjectVersion.versionId,
  );
  const hasPermissionToManage = useHasPermission(permissionCode);

  const dropdownItems = useMemo(() => {
    const result: ItemType[] = [];

    if (hasPermissionToManage) {
      result.push({
        key: 'cloneDescription',
        label: <DropdownItem>{t('button.cloneDescription')}</DropdownItem>,
      });
    }

    if (hasPermissionToManageVersion) {
      if (!algorithmsCount) {
        result.push({
          key: 'cloneDescriptionAlgorithms',
          label: (
            <DropdownItem>
              {t('button.cloneDescriptionAlgorithms')}
            </DropdownItem>
          ),
        });
      }

      result.push(
        {
          key: 'createVersion',
          label: <DropdownItem>{t('button.createVersion')}</DropdownItem>,
        },
        {
          key: 'deleteVersion',
          disabled: isLocked(lockData),
          label: (
            <PopconfirmDelete
              stopClickPropagation
              onDelete={handleDelete}
              genitiveModelName={deleteGenitiveModelName}
            >
              {t('button.deleteVersion.label')}
            </PopconfirmDelete>
          ),
        },
      );
    }

    if (dataObjectVersion.statusCode === 'ACTIVE') {
      result.push({
        key: 'openRelationsGraph',
        label: <DropdownItem>{t('button.openRelationsGraph')}</DropdownItem>,
      });
    }

    return result;
  }, [
    handleDelete,
    deleteGenitiveModelName,
    lockData,
    dataObjectVersion,
    hasPermissionToManageVersion,
    hasPermissionToManage,
    algorithmsCount,
  ]);

  const isInput = dataObjectVersion.dataObjectType === DataObjectType.IN;
  const isOutput = dataObjectVersion.dataObjectType === DataObjectType.OUT;
  const isIntermediate =
    dataObjectVersion.outputTypeCode === DataObjectOutputType.INTERMEDIATE;
  const isReport =
    dataObjectVersion.outputTypeCode === DataObjectOutputType.REPORT;

  const segmentOptions = useMemo(() => {
    const segments: ContentSegmentOption<DataObjectContentSegmentKey>[] = [
      {
        value: 'dimensions',
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.dimensions')}{' '}
            <Badge count={dataObjectVersion?.dimensions?.length} />
          </Flex>
        ),
        children: (
          <DimensionViewTable
            dataObjectView={dataObjectView}
            outputTypeCode={dataObjectVersion.outputTypeCode ?? undefined}
            dimensions={dataObjectVersion?.dimensions ?? []}
          />
        ),
        count: dataObjectVersion?.dimensions?.length,
      },
      {
        value: 'indicators',
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.indicators')}{' '}
            <Badge count={dataObjectVersion?.indicators?.length} />
          </Flex>
        ),
        children: (
          <DataObjectIndicatorDetailsTable
            dataObjectView={dataObjectView}
            indicators={dataObjectVersion?.indicators}
          />
        ),
        count: dataObjectVersion?.indicators?.length,
      },
    ];
    if (isInput) {
      segments.push({
        value: 'dataSources',
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.dataSources')}{' '}
            <Badge count={dataObjectVersion?.dataSources?.length} />
          </Flex>
        ),
        children: (
          <DataSourcesViewTable dataSources={dataObjectVersion?.dataSources} />
        ),
        count: dataObjectVersion?.dataSources?.length,
      });
    } else {
      if (!isIntermediate) {
        segments.push({
          value: 'validationRules',
          label: (
            <Flex gap="small" align="center">
              {t('segment-option.validationRules')}{' '}
              <Badge count={dataObjectVersion?.validationRules?.length} />
            </Flex>
          ),
          children: (
            <ValidationRulesViewTable
              validationRules={dataObjectVersion?.validationRules}
            />
          ),
          count: dataObjectVersion?.validationRules?.length,
        });
      }
    }

    segments.push({
      value: 'algorithms',
      label: (
        <Flex gap="small" align="center">
          {t('segment-option.algorithms')} <Badge count={algorithmsCount} />
        </Flex>
      ),
      children: (
        <AlgorithmsTable
          onAlgorithmsCountChange={setAlgorithmsCount}
          requiredKeys={dataObjectVersion?.requiredKeys}
        />
      ),
      count: algorithmsCount,
    });

    if (!isInput) {
      segments.push({
        value: 'alternativeAlgorithmSets',
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.alternativeAlgorithmSets')}{' '}
            <Badge count={dataObjectVersion?.alternativeAlgorithmSets.length} />
          </Flex>
        ),
        children: (
          <AlternativeAlgorithmsViewTable
            dataObjectVersionId={dataObjectVersion.versionId}
            data={dataObjectVersion.alternativeAlgorithmSets}
          />
        ),
        count: dataObjectVersion?.requiredKeys?.length,
      });

      if (DataObjectOutputType.REPORT === dataObjectVersion.outputTypeCode) {
        segments.push({
          value: 'requiredKeys',
          label: (
            <Flex gap="small" align="center">
              {t('segment-option.requiredKeys')}{' '}
              <Badge count={dataObjectVersion?.requiredKeys.length} />
            </Flex>
          ),
          children: (
            <KeysViewTable
              data={dataObjectVersion.requiredKeys}
              indicatorStatus={dataObjectVersion.indicatorStatus}
            />
          ),
          count: dataObjectVersion?.requiredKeys?.length,
        });
      }
    }

    if (!isIntermediate) {
      const calendarCount =
        dataObjectVersion?.execution?.executionCalendar?.length ||
        dataObjectVersion?.execution?.executionPeriods?.length;
      segments.push({
        value: 'execution',
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.execution')} <Badge count={calendarCount} />
          </Flex>
        ),
        children: (
          <CalendarWrapper>
            <CalendarView
              dataObjectView={dataObjectView}
              execution={dataObjectVersion.execution}
            />
          </CalendarWrapper>
        ),
        count: calendarCount,
      });
      const deadlineCount = dataObjectVersion?.executionDeadlines?.length;
      segments.push({
        value: 'executionDeadlines',
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.executionDeadlines')}{' '}
            <Badge count={deadlineCount} />
          </Flex>
        ),
        children: (
          <DeadlinesViewTable
            dataObjectVersionId={dataObjectVersion?.versionId ?? undefined}
            deadlines={dataObjectVersion?.executionDeadlines}
            onDeadlineValidate={() => {
              setDeadlineValidationErrors([]);
              resetAlert();
            }}
            onDeadlineValidateErrors={(errors: ApiError[]) => {
              setDeadlineValidationErrors(errors);
            }}
            onDeadlineValidateSuccess={() => {
              setSuccess(t('alert.success.validated-deadlines'));
            }}
          />
        ),
        count: deadlineCount,
      });
    }

    return segments;
  }, [
    dataObjectVersion?.dimensions,
    dataObjectVersion.outputTypeCode,
    dataObjectVersion?.indicators,
    algorithmsCount,
    dataObjectVersion.requiredKeys,
    dataObjectVersion?.executionDeadlines,
    dataObjectVersion.versionId,
    dataObjectVersion?.dataSources,
    dataObjectVersion?.validationRules,
    dataObjectVersion.execution,
    dataObjectVersion.alternativeAlgorithmSets,
    dataObjectVersion.indicatorStatus,
    dataObjectView,
    isInput,
    isIntermediate,
    setDeadlineValidationErrors,
    resetAlert,
    setSuccess,
  ]);

  const infoIconUrl = props.dataObjectView.isInput
    ? InfoIconUrl.INPUT_DATA_OBJECT_DESCRIPTIONS_DETAILS
    : InfoIconUrl.OUTPUT_DATA_OBJECT_DESCRIPTIONS_DETAILS;

  return (
    <DataObjectDescriptionVersionStoreProvider
      initialData={{
        dataObjectDescriptionVersion: dataObjectVersion,
        openTestCalculate,
        openTestValidate,
      }}
    >
      {isCloneAlgorithmOpen && (
        <CloneAlgorithmsModal
          onSuccess={(data) => handleSuccess(data)}
          onClose={() => setIsCloneAlgorithmOpen(false)}
        />
      )}
      <DetailLayout
        infoIconUrl={infoIconUrl}
        type="detail"
        breadCrumb={breadCrumb}
        footerContent={<ContentSegmentTotalEntries segments={segmentOptions} />}
        headerConfig={{
          actions: (
            <>
              <DataObjectFormTypeBadge formType={dataObjectVersion.formType} />
              <DataObjectVersionSelect
                versions={dataObjectVersion.allVersions || []}
                activeVersionId={dataObjectVersion.versionId}
                onChange={handleVersionChange}
              />
              <DataObjectExcelActions
                id={dataObjectVersion.versionId}
                isInput={isInput}
                isReport={
                  dataObjectVersion.outputTypeCode ===
                  DataObjectOutputType.REPORT
                }
                isIntermediate={
                  dataObjectVersion.outputTypeCode ===
                  DataObjectOutputType.INTERMEDIATE
                }
                dataObjectType={dataObjectVersion.dataObjectType}
                hasPermissionToManage={hasPermissionToManageVersion}
              />
              {actions}
              {hasPermissionToManageVersion && (
                <ActionButton
                  disabled={isLocked(lockData)}
                  type="mainAction"
                  layoutType="detail"
                  onClick={() =>
                    navigateToEdit(getDataObjectPanelNavigationParams())
                  }
                />
              )}
            </>
          ),
          dropdownMenu: {
            items: dropdownItems,
            onClick: ({ key }) => {
              switch (key) {
                case 'cloneDescription':
                  return onClickClone({
                    entityType: 'description',
                    segment: getQueryParam('segment'),
                  });
                case 'cloneDescriptionAlgorithms':
                  return onClickCloneAlgorithm();
                case 'createVersion':
                  return onClickClone({
                    entityType: 'version',
                    segment: getQueryParam('segment'),
                  });
                case 'openRelationsGraph':
                  return navigateToRelationsGraph();
              }
            },
          },
        }}
        side={{
          header: (
            <Padding>
              <ObjectHeader
                upperText={dataObjectVersion.code}
                lowerText={dataObjectVersion.name}
              />
            </Padding>
          ),
          content: (
            <Padding>
              <DescriptionsWrapper>
                <Descriptions
                  column={1}
                  items={[
                    ...(isOutput
                      ? [
                          {
                            key: 'outputTypeCode',
                            label: t('outputTypeCode.label'),
                            children: t(
                              'outputTypeCode.' +
                                dataObjectVersion.outputTypeCode,
                            ),
                          },
                        ]
                      : []),
                    ...(isIntermediate
                      ? [
                          {
                            key: 'outputDataObject',
                            label: t('outputDataObject.label'),
                            children: formatCodeName({
                              code: dataObjectVersion.outputDataObjectCode,
                              name: dataObjectVersion.outputDataObjectName,
                            }),
                          },
                        ]
                      : []),
                  ]}
                />
                {isOutput && (
                  <Bleed direction="horizontal">
                    <Divider />
                  </Bleed>
                )}
                <Descriptions
                  column={1}
                  items={[
                    {
                      key: 'groupAndSubgroup',
                      label: t(
                        'groupAndSubgroup.' + dataObjectView.dataObjectType,
                      ),
                      children: dataObjectVersion?.groupSubgroupName || '',
                    },
                    {
                      key: 'domain',
                      label: t('domain.label'),
                      children:
                        systemClassifiers.DOMAIN.systemClassifierItems?.find(
                          (item) => item.id === dataObjectVersion.domainId,
                        )?.name || '',
                    },
                    ...(isInput
                      ? [
                          {
                            key: 'source',
                            label: t('source.label'),
                            children: (dataObjectVersion?.sourceIds || [])
                              .map(
                                (sourceId) =>
                                  systemClassifiers.DATA_ORIGIN.systemClassifierItems?.find(
                                    (item) => item.id === sourceId,
                                  )?.name || '',
                              )
                              .join(', '),
                          },
                          {
                            key: 'dataProviderGroup',
                            label: t('dataProviderGroup.label'),
                            children:
                              systemClassifiers.DATA_PROVIDER_GROUP.systemClassifierItems?.find(
                                (item) =>
                                  item.id ===
                                  dataObjectVersion.dataProviderGroupId,
                              )?.name || '',
                          },
                        ]
                      : []),
                    {
                      key: 'accessGroup',
                      label: t('accessGroup.label'),
                      children: dataObjectVersion?.accessGroupName || '',
                    },
                    {
                      key: 'frequency',
                      label: t('frequency.label'),
                      children:
                        systemClassifiers.FREQUENCY.systemClassifierItems?.find(
                          (item) => item.id === dataObjectVersion.frequencyId,
                        )?.name || '',
                    },
                    ...(isInput
                      ? [
                          {
                            key: 'externalCode',
                            label: t('externalCode.label'),
                            children: dataObjectVersion?.externalCode || '',
                          },
                        ]
                      : []),
                    {
                      key: 'responsible',
                      label: t('responsible.label'),
                      children: dataObjectVersion?.responsibleUsername || '',
                    },
                    {
                      key: 'confidential',
                      label: t('confidential.label'),
                      children: dataObjectVersion?.confidential
                        ? t('confidential.yes')
                        : t('confidential.no'),
                    },
                    ...(isOutput
                      ? [
                          {
                            key: 'subjectDimension',
                            label: t('subjectDimension.label'),
                            children: dataObjectVersion.subjectDimension
                              ? t('subjectDimension.yes')
                              : t('subjectDimension.no'),
                          },
                        ]
                      : []),
                    {
                      key: 'description',
                      label: t('description.label'),
                      children: dataObjectVersion.description || '',
                    },
                  ]}
                />
                <Bleed direction="horizontal">
                  <Divider>
                    {dataObjectVersion.versionNumber +
                      '.' +
                      statusName?.toUpperCase() +
                      ' ' +
                      getValidPeriodString(dataObjectVersion.validPeriod)}
                  </Divider>
                </Bleed>
                <Descriptions
                  column={1}
                  items={[
                    ...(isInput
                      ? [
                          {
                            key: 'aggregateValues',
                            label: t('aggregateValues.label'),
                            children: dataObjectVersion.aggregateValues
                              ? t('aggregateValues.yes')
                              : t('aggregateValues.no'),
                          },
                        ]
                      : []),
                    ...(isReport
                      ? [
                          {
                            key: 'recipients',
                            label: t('recipients.label'),
                            children: (dataObjectVersion?.recipientIds || [])
                              .map(
                                (recipientId) =>
                                  systemClassifiers.REPORT_RECIPIENT.systemClassifierItems?.find(
                                    (item) => item.id === recipientId,
                                  )?.name || '',
                              )
                              .join(', '),
                          },
                          {
                            key: 'indicatorStatus',
                            label: t('indicatorStatus.label'),
                            children: dataObjectVersion.indicatorStatus
                              ? t('indicatorStatus.yes')
                              : t('indicatorStatus.no'),
                          },
                          {
                            key: 'confidentialityStatus',
                            label: t('confidentialityStatus.label'),
                            children:
                              systemClassifiers.CONFIDENTIALITY_STATUS.systemClassifierItems?.find(
                                (item) =>
                                  item.id ===
                                  dataObjectVersion.confidentialityStatusId,
                              )?.name || '',
                          },
                        ]
                      : []),
                    {
                      key: 'versionDescription',
                      label: t('versionDescription.label'),
                      children: dataObjectVersion.versionDescription || '',
                    },
                  ]}
                />
              </DescriptionsWrapper>
            </Padding>
          ),
          footer: (
            <LockedBy
              id={dataObjectVersion.versionId}
              entity={dataObjectVersion}
              objectType={ObjectType.DataObjectDescriptionVersion}
            />
          ),
        }}
        alert={
          <>
            <ErrorAlertWithDrawer validationMessagesMap={mutationErrors} />
            <ErrorAlertWithDrawer
              errorAction={t('deadlineValidation')}
              renderErrorHeading={displayDeadlineValidationType}
              validationMessagesMap={deadlineValidationErrors}
            />

            <ValidateRequiredKeys
              isModalOpen={validateKeysModalOpen}
              onModalClose={() => setValidateKeysModalOpen(false)}
            />
          </>
        }
      >
        <ContentWrapper>
          <ContentSegments segments={segmentOptions} />
        </ContentWrapper>
      </DetailLayout>
    </DataObjectDescriptionVersionStoreProvider>
  );
};

const ContentWrapper = styled(Padding)`
  width: 100%;
  height: 100%;
  min-width: fit-content;
  min-height: fit-content;
`;

export const DescriptionsWrapper = styled.div`
  .ant-descriptions-item {
    white-space: pre-line;
  }
  .ant-divider {
    overflow: hidden;
  }
`;

const CalendarWrapper = styled.div`
  max-width: 100%;
  width: 100%;
`;
